/**
 * SahibCompanion (صَاحِب — "companion") — the per-page AI helper.
 *
 * Auto-detects the current dashboard route and surfaces a sticky pill in the
 * top-right that reads "Ask Iqra" — the universal concierge entry point.
 * Click → opens a right-slide drawer with persona tabs for the page's
 * contextual specialists (Maryam on /agreements, Nouh on /erm, etc.).
 *
 * v167.x — split from 638 → ~170 lines by extracting:
 *   • ./sahib/useSahibActions.js — all message + tool + plan handlers
 *   • ./sahib/SahibDrawer.jsx    — the drawer body (header / tabs /
 *                                  chat scroll / input)
 *
 * Different from Iqra (universal concierge): Sahib is CONTEXTUAL — the
 * drawer disappears the moment the user changes route. Iqra is always
 * available as the first persona tab.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { buildGreeting } from '@/lib/i18nNav';
import useSahibActions from './sahib/useSahibActions';
import SahibDrawer from './sahib/SahibDrawer';
import useIqraHealth from '@/hooks/useIqraHealth';

const IQRA_TAB = {
  id: 'iqra',
  english_name: 'Iqra',
  arabic_name: 'إِقْرَأ',
  english_role: 'AI Concierge',
  role: 'Universal Concierge',
  tagline: 'Reads users. Drafts change requests. The platform listens through her.',
};

export default function SahibCompanion() {
  const loc = useLocation();
  const navTo = useNavigate();
  const [meta, setMeta] = useState(null); // { page_label, primary, secondary }
  const [open, setOpen] = useState(false);
  const [activePersona, setActivePersona] = useState('iqra');
  const [msg, setMsg] = useState('');
  const [history, setHistory] = useState({});
  const [sessions, setSessions] = useState({});
  const [busy, setBusy] = useState(false);
  const [shotData, setShotData] = useState(null);
  const [shotBusy, setShotBusy] = useState(false);
  const [journalSent, setJournalSent] = useState(false);
  const inputRef = useRef(null);

  // Lookup the persona(s) for this route on every navigation.
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on async fetch */
  useEffect(() => {
    let cancelled = false;
    if (!loc.pathname.startsWith('/dashboard')) {
      setMeta(null); setOpen(false); return undefined;
    }
    api.post('/ai/companion/lookup', { path: loc.pathname })
      .then(({ data }) => { if (!cancelled) { setMeta(data); setActivePersona('iqra'); } })
      .catch(() => { /* silent — pill just won't render */ });
    return () => { cancelled = true; };
  }, [loc.pathname]);
  /* eslint-enable */

  // Build the tab list: Iqra first, then this page's specialists.
  const tabPersonas = useMemo(() => {
    if (!meta) return [];
    const seen = new Set(['iqra']);
    const out = [IQRA_TAB];
    for (const p of [meta.primary, ...(meta.secondary || [])]) {
      if (p && !seen.has(p.id)) { seen.add(p.id); out.push(p); }
    }
    return out;
  }, [meta]);

  const activeMeta = useMemo(
    () => tabPersonas.find((p) => p.id === activePersona) || tabPersonas[0],
    [tabPersonas, activePersona],
  );
  const activeMsgs = history[activePersona] || [];

  // Auto-greet when the user switches to a persona or opens the drawer.
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on greeting seed */
  useEffect(() => {
    if (!open || !activeMeta) return;
    setHistory((h) => {
      if ((h[activeMeta.id] || []).length > 0) return h;
      const line = buildGreeting(
        activeMeta.english_name,
        activeMeta.arabic_name,
        activeMeta.english_role,
        meta?.page_label,
      );
      return { ...h, [activeMeta.id]: [{ role: 'ai', text: line }] };
    });
  }, [open, activeMeta?.id, meta?.page_label]);
  /* eslint-enable */

  // All side-effect-y handlers live in this hook so the parent stays small.
  const actions = useSahibActions({
    loc, navTo, meta, activePersona, activeMeta, sessions,
    setOpen, setMsg, setHistory, setSessions, setBusy,
    setShotData, setShotBusy, setJournalSent,
    inputRef,
  });
  // The hook returns lean callbacks; the drawer also needs the
  // setShotData passthrough for the "clear screenshot" affordance.
  const drawerActions = { ...actions, setShotData };

  // ── Iqra live health (drives the FAB dot color) ──
  const health = useIqraHealth();

  if (!meta || !loc.pathname.startsWith('/dashboard')) return null;

  return (
    <>
      {/* v167.2 — Floating Action Button (FAB) in the bottom-right
          corner, à la Intercom/Crisp. The status dot is wired to a
          real /api/iqra/health poll so prospects see a live "AI is
          actually online" trust signal without clicking. */}
      <button
        data-testid="sahib-trigger"
        data-iqra-status={health.status}
        onClick={() => { setActivePersona('iqra'); setOpen(true); }}
        className="fixed z-[70] bottom-20 right-4 md:bottom-6 md:right-6
                   inline-flex items-center gap-2.5 h-12 pl-1.5 pr-5 rounded-full
                   bg-[#0A0A0A] text-[#8B3FC5]
                   border border-[#8B3FC5]/70 hover:border-[#8B3FC5]
                   shadow-[0_8px_32px_-6px_rgba(174,82,234,0.55)]
                   hover:shadow-[0_12px_40px_-4px_rgba(174,82,234,0.85)]
                   hover:scale-[1.03] active:scale-[0.98]
                   transition-all text-xs uppercase tracking-[0.22em] font-bold"
        title={`Ask Iqra — ${health.label}. Universal AI concierge; switch to a specialist inside.`}
        aria-label={`Open Iqra AI concierge — ${health.label}`}
      >
        <span className="relative inline-flex h-9 w-9 rounded-full bg-gradient-to-br from-[#8B3FC5] to-[#5E1F8B] items-center justify-center shrink-0">
          <Sparkles size={15} className="text-[#0A0A0A]"/>
          <span
            data-testid="sahib-health-dot"
            className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[#0A0A0A]
                        ${health.tone.dot}
                        ${health.status === 'healthy' ? 'animate-pulse' : ''}`}
          />
        </span>
        <span className="hidden sm:inline">Ask <span className="text-[#8B3FC5] font-extrabold tracking-[0.28em]">Iqra</span></span>
      </button>

      {open && (
        <SahibDrawer
          meta={meta}
          activeMeta={activeMeta}
          activePersona={activePersona}
          tabPersonas={tabPersonas}
          activeMsgs={activeMsgs}
          history={history}
          busy={busy}
          msg={msg}
          setMsg={setMsg}
          shotData={shotData}
          shotBusy={shotBusy}
          journalSent={journalSent}
          onClose={() => setOpen(false)}
          onSelectPersona={setActivePersona}
          actions={drawerActions}
          inputRef={inputRef}
        />
      )}
    </>
  );
}
